import { useEffect, useState } from "react";

import { Alert } from "../components/Alert";
import { Badge } from "../components/Badge";
import { Card, Empty } from "../components/Card";
import { PageHead } from "../components/PageHead";
import { fetchSites } from "../lib/api";
import { cx } from "../lib/cx";
import { messageOf } from "../lib/errors";
import { isHttpsURL } from "../lib/format";
import { PERM, can } from "../lib/rbac";
import type { Session, Site } from "../lib/types";

/**
 * 서비스 갤러리 (`/gallery`).
 *
 * 로그인한 사람이 사내 서비스로 이동하는 화면입니다. 타일은 `entryUrl` 로 나가는 외부 링크이고,
 * 세션은 그대로 유지됩니다(각 서비스가 미로그인 사용자를 다시 이 콘솔로 위임합니다).
 */
export function GalleryScreen({ session, revision }: { session: Session; revision: number }) {
  const [sites, setSites] = useState<Site[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    // `alive` 가드: 응답이 늦게 도착한 이전 요청이 새 상태를 덮어쓰지 않게 합니다.
    let alive = true;
    fetchSites()
      .then((rows) => {
        if (!alive) return;
        setSites(rows);
        setLoadError(null);
      })
      .catch((err: unknown) => {
        if (alive) setLoadError(messageOf(err));
      });
    return () => {
      alive = false;
    };
  }, [revision]);

  return (
    <>
      <PageHead title="서비스" sub="사내 서비스로 이동합니다. 로그인 상태가 그대로 유지됩니다." />

      {loadError ? <Alert kind="error">{loadError}</Alert> : null}

      {!loadError && sites.length === 0 ? (
        <Card>
          <Empty>
            {can(session, PERM.siteManage)
              ? "등록된 서비스가 없습니다. 카탈로그 메뉴에서 추가해 주세요."
              : "등록된 서비스가 없습니다. 플랫폼 관리자에게 문의해 주세요."}
          </Empty>
        </Card>
      ) : null}

      {sites.length > 0 ? (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-[16px]">
          {sites.map((site) => (
            <Tile key={site.siteId} site={site} />
          ))}
        </div>
      ) : null}
    </>
  );
}

const TILE_BASE =
  "flex flex-col gap-[12px] rounded-[20px] border border-grey-100 bg-white p-[20px] " +
  "text-inherit shadow-soft transition-[border-color,transform] duration-[120ms]";
const TILE_LINK =
  "hover:border-blue-500 hover:-translate-y-[2px] hover:no-underline";

function Tile({ site }: { site: Site }) {
  /*
   * 서버가 https 절대 URL 만 저장하지만(`domain.ValidateSiteEntryURL`) **렌더 직전에 재확인**합니다 —
   * 스키마가 바뀌거나 DB 를 직접 수정한 행이 섞일 수 있고, 이 화면은 운영자 입력을 모든 구성원에게
   * 보여주기 때문입니다. 안전하지 않으면 링크로 만들지 않고 "주소 오류" 배지만 남깁니다.
   */
  const safe = isHttpsURL(site.entryUrl);

  const inner = (
    <>
      <div className="flex items-center gap-[10px]">
        <span
          aria-hidden="true"
          className="inline-flex h-[40px] w-[40px] shrink-0 items-center justify-center rounded-xl bg-grey-050 text-[20px]"
        >
          {site.icon || "▦"}
        </span>
        <div className="min-w-0">
          <div className="text-[16px] font-bold tracking-[-0.02em]">{site.displayName}</div>
          <div className="text-[12px] font-semibold text-grey-400">{site.siteCode}</div>
        </div>
      </div>

      {site.description ? (
        // 설명 길이가 타일 높이를 흔들지 않게 3줄로 자릅니다.
        <p className="m-0 line-clamp-3 text-[13px] text-grey-600">{site.description}</p>
      ) : null}

      <div className="mt-auto flex flex-wrap items-center gap-[6px]">
        {site.ssoEnabled ? (
          <Badge tone="brand">통합 로그인</Badge>
        ) : (
          <Badge tone="muted">개별 로그인</Badge>
        )}
        {site.granted ? <Badge tone="ok">권한 있음</Badge> : null}
        {safe ? null : <Badge tone="danger">주소 오류</Badge>}
      </div>
    </>
  );

  if (!safe) return <div className={TILE_BASE}>{inner}</div>;

  return (
    // 외부 서비스로 나가는 링크라 rel 로 opener 를 끊습니다(Referrer 는 응답 헤더가 이미 차단).
    <a
      className={cx(TILE_BASE, TILE_LINK)}
      href={site.entryUrl}
      rel="noopener noreferrer"
      target="_blank"
    >
      {inner}
    </a>
  );
}
